import { isAxiosError } from "axios";
import { queryOptions } from "@tanstack/react-query";
import { getTemplateJob, type TemplateJobResponse } from "@/features/templates/api/templatesApi";
import { rmsClient } from "@/shared/api/clients";
import type { NarrativeTemplate } from "../../../shared/interfaces/ReportGenReport";

export const templateKeys = {
  all: ["templates"] as const,
  jobs: () => [...templateKeys.all, "job"] as const,
  job: (jobId: string) => [...templateKeys.jobs(), jobId] as const,
  station: (stationId: string) =>
    [...templateKeys.all, "station", stationId] as const,
};

/**
 * The station's custom report templates (drives the report-type picker).
 * Template-upload flows bust this by invalidating templateKeys.all after
 * delete/rename/sign-off — a background refetch then replaces the list,
 * matching the old null-then-refetch context cycle.
 */
export function stationTemplatesQuery(stationId: string) {
  return queryOptions<NarrativeTemplate[]>({
    queryKey: templateKeys.station(stationId),
    queryFn: async () => {
      const response = await rmsClient.get("/templates", {
        params: { stationId },
      });
      return response.data as NarrativeTemplate[];
    },
    staleTime: 5 * 60_000,
    gcTime: 30 * 60_000,
  });
}

export function templateJobQuery(jobId: string) {
  return queryOptions<TemplateJobResponse>({
    queryKey: templateKeys.job(jobId),
    queryFn: ({ signal }) => getTemplateJob(jobId, signal),
    // The transition effect owns failure handling — retrying here would
    // delay the "processing failed" feedback the old poller gave at once.
    retry: false,
    staleTime: 0,
    gcTime: 0,
  });
}

/**
 * Pure poll-interval predicate, mirroring the old setInterval semantics:
 * stop on completed/failed and on HTTP errors (the page surfaces them);
 * keep polling through network blips (the old `if (!err.response) return`).
 */
export function templateJobPollInterval(
  data: TemplateJobResponse | undefined,
  error: unknown,
): number | false {
  if (data?.status === "completed" || data?.status === "failed") {
    return false;
  }
  if (error) {
    if (isAxiosError(error) && error.response) {
      return false;
    }
    return 5000;
  }
  return 5000;
}
